import React, { Suspense, lazy } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useLocation,
} from "react-router-dom";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import ProtectedRoute from "./components/ProtectedRoute";

// Eager: on the critical public path (landing / login), keep these in the
// main chunk so first paint isn't gated on an extra network round-trip.
import Login from "./components/Login";
import PortfolioHome from "./pages/PortfolioHome";

// Lazy: every other route is code-split into its own chunk and only fetched
// when navigated to. This keeps the initial JS bundle small so the public
// landing page and login become interactive quickly.
// Layout (with Sidebar, Header, modals) only renders for authenticated users,
// so it's lazy too — keeping logged-in UI out of the anonymous landing bundle.
const Layout = lazy(() => import("./components/Layout"));
const EmployeeDashboard = lazy(() => import("./pages/employee/dashboard"));
const BrowserTimeTracking = lazy(() => import("./pages/employee/browser-tracking"));
const AdminDashboard = lazy(() => import("./pages/admin/dashboard"));
const AdminCalendar = lazy(() => import("./pages/admin/calendar"));
const UserManagement = lazy(() => import("./pages/shared/UserManagement"));
const SystemOwnerDashboard = lazy(() => import("./pages/system_owner/dashboard"));
const DownloadDataPage = lazy(() => import("./pages/system_owner/download-data"));
const AiAnalyzer = lazy(() => import("./pages/admin/ai-analyzer"));
const BillingHours = lazy(() => import("./pages/employee/billing"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const DesktopAuth = lazy(() => import("./pages/DesktopAuth"));
const OrganizationSettingsPage = lazy(() => import("./pages/system_owner/settings"));
const AdminManagePage = lazy(() => import("./pages/admin/manage"));
const BillingCredits = lazy(() => import("./pages/admin/BillingCredits"));
const BillingStatementPage = lazy(() => import("./pages/admin/billing-statement"));
const CreditsHistory = lazy(() => import("./pages/system/CreditsHistory"));
const SubscriptionsOverviewPage = lazy(() => import("./pages/system/subscriptions"));
const SubscriptionDetailView = lazy(
  () => import("./pages/system/subscriptions/SubscriptionDetailView")
);
const BillingOverviewPage = lazy(() => import("./pages/system/billing-overview"));
const GettingStarted = lazy(() => import("./pages/GettingStarted"));
const RegisterOrg = lazy(() => import("./pages/public/register-org"));
const ForgotPassword = lazy(() => import("./pages/ForgotPassword"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const ChatbotWidget = lazy(() => import("./components/Chatbot"));

/** Lightweight fallback shown while a lazily-loaded route chunk downloads. */
const RouteFallback: React.FC = () => (
  <div
    style={{
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      minHeight: "100vh",
    }}
  >
    <div
      style={{
        width: 32,
        height: 32,
        border: "3px solid #e5e1dc",
        borderTopColor: "#3d3a37",
        borderRadius: "50%",
        animation: "rf-spin 0.8s linear infinite",
      }}
    />
    <style>{`@keyframes rf-spin { to { transform: rotate(360deg); } }`}</style>
  </div>
);

/** Unauthenticated unknown paths → login, preserving deep-link return URL */
const GuestRedirectToLogin: React.FC = () => {
  const location = useLocation();
  const fullPath = `${location.pathname}${location.search}`;
  const to = `/login?redirect=${encodeURIComponent(fullPath)}`;
  return <Navigate to={to} replace />;
};

/** Route-level split: render browser tracking page or desktop (Electron) page */
const EmployeeTimeTrackingRoute: React.FC = () => {
  const { organization } = useAuth();
  const browserTrackingEnabled = organization?.settings?.browserTracking === true;
  return browserTrackingEnabled ? <BrowserTimeTracking /> : <EmployeeDashboard />;
};

const AppContent: React.FC = () => {
  const { isAuthenticated, user } = useAuth();

  const getDefaultRoute = () => {
    if (user?.role === "system_owner") {
      return "/system";
    } else if (user?.role === "org_admin") {
      return "/admin/dashboard";
    } else {
      return "/employee/time-tracking";
    }
  };

  return (
    <Router>
      <Suspense fallback={<RouteFallback />}>
        {isAuthenticated && <ChatbotWidget />}
        <Routes>
        {/* Public routes - accessible with or without auth */}
        <Route path="/privacy-policy" element={<PrivacyPolicy />} />
        <Route path="/register-org" element={<RegisterOrg />} />
        <Route path="/desktop-auth" element={<DesktopAuth />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />

        {isAuthenticated ? (
          <>
            <Route
              path="/login"
              element={<Navigate to={getDefaultRoute()} replace />}
            />
            <Route path="/getting-started" element={<GettingStarted />} />
            <Route path="/" element={<Layout />}>
              <Route
                index
                element={<Navigate to={getDefaultRoute()} replace />}
              />

              {/* System Owner Routes */}
              <Route
                path="system"
                element={
                  <ProtectedRoute requireRole="system_owner">
                    <SystemOwnerDashboard />
                  </ProtectedRoute>
                }
              />
              <Route
                path="system/users"
                element={
                  <ProtectedRoute requireRole="system_owner">
                    <UserManagement />
                  </ProtectedRoute>
                }
              />
              <Route
                path="system/org-settings"
                element={
                  <ProtectedRoute requireRole="system_owner">
                    <OrganizationSettingsPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="system/download-data"
                element={
                  <ProtectedRoute requireRole="system_owner">
                    <DownloadDataPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="system/credits"
                element={
                  <ProtectedRoute requireRole="system_owner">
                    <BillingOverviewPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="system/subscriptions"
                element={
                  <ProtectedRoute requireRole="system_owner">
                    <BillingOverviewPage />
                  </ProtectedRoute>
                }
              />
              {/* Legacy standalone routes kept for fallback. */}
              <Route
                path="system/credits-legacy"
                element={
                  <ProtectedRoute requireRole="system_owner">
                    <CreditsHistory />
                  </ProtectedRoute>
                }
              />
              <Route
                path="system/subscriptions-legacy"
                element={
                  <ProtectedRoute requireRole="system_owner">
                    <SubscriptionsOverviewPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="system/subscriptions/:orgId"
                element={
                  <ProtectedRoute requireRole="system_owner">
                    <SubscriptionDetailView />
                  </ProtectedRoute>
                }
              />

              {/* Admin Routes */}
              <Route
                path="admin/dashboard"
                element={
                  <ProtectedRoute requireRole="org_admin">
                    <AdminDashboard />
                  </ProtectedRoute>
                }
              />
              <Route
                path="admin/calendar"
                element={
                  <ProtectedRoute requireRole="org_admin">
                    <AdminCalendar />
                  </ProtectedRoute>
                }
              />
              <Route
                path="admin/calendar/:userId"
                element={
                  <ProtectedRoute requireRole="org_admin">
                    <AdminCalendar />
                  </ProtectedRoute>
                }
              />
              <Route
                path="admin/calendar/:userId/:date/details"
                element={
                  <ProtectedRoute requireRole="org_admin">
                    <AdminCalendar />
                  </ProtectedRoute>
                }
              />
              <Route
                path="admin/setting"
                element={
                  <ProtectedRoute requireRole="org_admin">
                    <AdminManagePage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="admin/billing"
                element={
                  <ProtectedRoute requireRole="org_admin">
                    <BillingStatementPage />
                  </ProtectedRoute>
                }
              />
              {/* Legacy standalone Billing page kept for fallback. */}
              <Route
                path="admin/billing-legacy"
                element={
                  <ProtectedRoute requireRole="org_admin">
                    <BillingCredits />
                  </ProtectedRoute>
                }
              />
              {/* Redirects from old URLs */}
              <Route
                path="admin/manage"
                element={<Navigate to="/admin/setting?tab=users" replace />}
              />
              <Route
                path="admin/users"
                element={<Navigate to="/admin/setting?tab=users" replace />}
              />
              <Route
                path="admin/settings"
                element={<Navigate to="/admin/setting?tab=organization" replace />}
              />
              <Route
                path="admin/billing-settings"
                element={<Navigate to="/admin/setting?tab=billing" replace />}
              />
              <Route
                path="admin/ai-analyzer"
                element={
                  <ProtectedRoute requireRole="org_admin">
                    <AiAnalyzer />
                  </ProtectedRoute>
                }
              />
              <Route
                path="admin/subscriptions"
                element={
                  <ProtectedRoute requireRole="org_admin">
                    <BillingStatementPage />
                  </ProtectedRoute>
                }
              />

              {/* Employee Routes */}
              <Route
                path="employee"
                element={<Navigate to="/employee/time-tracking" replace />}
              />
              <Route
                path="employee/time-tracking"
                element={
                  <ProtectedRoute requireRole="employee">
                    <EmployeeTimeTrackingRoute />
                  </ProtectedRoute>
                }
              />
              <Route
                path="employee/billing"
                element={
                  <ProtectedRoute requireRole="employee">
                    <BillingHours />
                  </ProtectedRoute>
                }
              />

              <Route
                path="*"
                element={<Navigate to={getDefaultRoute()} replace />}
              />
            </Route>
          </>
        ) : (
          <>
            <Route path="/" element={<PortfolioHome />} />
            <Route path="/getting-started" element={<GettingStarted />} />
            <Route path="/login" element={<Login />} />
            <Route path="*" element={<GuestRedirectToLogin />} />
          </>
        )}
        </Routes>
      </Suspense>
    </Router>
  );
};

function App() {
  return (
    <AuthProvider>
      <AppContent />
    </AuthProvider>
  );
}

export default App;
